import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Activity, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { primarySession } from "@/lib/sessions";
import InsightsGuide from "./InsightsGuide";
import YearReviewDialog from "./YearReviewDialog";
import MonthReadyBanner from "./MonthReadyBanner";
import { prevMonth } from "./reviewStats";

const DAY_MS = 86400000;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const money = (n) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const tone = (n) => (n > 0 ? "text-emerald-700" : n < 0 ? "text-rose-600" : "text-slate-600");
const SESSION_COLS = ["sydney", "tokyo", "london", "overlap", "newyork", "off"];
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0];

function cellClass(day, maxAbs) {
  if (!day) return "bg-slate-100";
  const k = maxAbs ? Math.abs(day.pnl) / maxAbs : 0;
  if (day.pnl > 0) return k > 0.66 ? "bg-emerald-600" : k > 0.33 ? "bg-emerald-400" : "bg-emerald-200";
  if (day.pnl < 0) return k > 0.66 ? "bg-rose-600" : k > 0.33 ? "bg-rose-400" : "bg-rose-200";
  return "bg-slate-300";
}

function Chip({ label, value, cls = "text-slate-900", testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
      {label} <span className={`font-mono font-semibold ${cls}`}>{value}</span>
    </span>
  );
}

function useYearData(entries, year) {
  return useMemo(() => {
    const days = {};
    entries.filter((e) => (e.traded_on || "").startsWith(String(year))).forEach((e) => {
      const d = (days[e.traded_on] ||= { pnl: 0, trades: [] });
      d.pnl += Number(e.pnl) || 0;
      d.trades.push(e);
    });
    const start = new Date(year, 0, 1);
    start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
    const weeks = [];
    for (let w = new Date(start); w <= new Date(year, 11, 31); w = new Date(w.getTime() + 7 * DAY_MS)) {
      weeks.push(Array.from({ length: 7 }, (_, i) => new Date(w.getFullYear(), w.getMonth(), w.getDate() + i)));
    }
    const vals = Object.values(days);
    const weekTotals = weeks.map((wk) => wk.reduce((s, d) => s + (days[iso(d)]?.pnl || 0), 0)).filter((_, i) => weeks[i].some((d) => days[iso(d)]));
    return {
      days, weeks,
      maxAbs: Math.max(0, ...vals.map((v) => Math.abs(v.pnl))),
      total: vals.reduce((s, v) => s + v.pnl, 0),
      trades: vals.reduce((s, v) => s + v.trades.length, 0),
      win: vals.filter((v) => v.pnl > 0).length,
      loss: vals.filter((v) => v.pnl < 0).length,
      bestDay: vals.length ? Math.max(...vals.map((v) => v.pnl)) : 0,
      worstDay: vals.length ? Math.min(...vals.map((v) => v.pnl)) : 0,
      bestWeek: weekTotals.length ? Math.max(...weekTotals) : 0,
      worstWeek: weekTotals.length ? Math.min(...weekTotals) : 0,
    };
  }, [entries, year]);
}

function YearGrid({ data, year, selected, onSelect, t, lang }) {
  const monthFmt = new Intl.DateTimeFormat(lang, { month: "short" });
  const dayFmt = new Intl.DateTimeFormat(lang, { weekday: "short" });
  return (
    <div className="overflow-x-auto pb-2">
      <div className="inline-flex gap-[2px]">
        <div className="flex flex-col gap-[2px] pt-4 pr-1 text-[9px] text-slate-400">
          {WEEKDAYS.map((wd, i) => <div key={wd} className="h-[10px] leading-[10px]">{i % 2 === 0 ? dayFmt.format(new Date(2024, 0, wd === 0 ? 7 : wd)) : ""}</div>)}
        </div>
        {data.weeks.map((wk, wi) => (
          <div key={wi} className="flex flex-col gap-[2px]">
            <div className="h-4 text-[9px] text-slate-400 whitespace-nowrap">
              {wk.some((d) => d.getDate() === 1 && d.getFullYear() === year) ? monthFmt.format(wk.find((d) => d.getDate() === 1)) : ""}
            </div>
            {wk.map((d) => {
              const key = iso(d);
              const inYear = d.getFullYear() === year;
              const day = data.days[key];
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!inYear}
                  onClick={() => onSelect(key)}
                  title={`${key}${day ? ` · ${money(day.pnl)} · ${day.trades.length}` : ""}`}
                  data-testid={`insights-day-${key}`}
                  className={`h-[10px] w-[10px] rounded-[2px] transition-transform hover:scale-125 ${inYear ? cellClass(day, data.maxAbs) : "opacity-0"} ${selected === key ? "ring-2 ring-blue-800 ring-offset-1" : ""}`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-rose-400" /> {t("insights.loss")}</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-400" /> {t("insights.profit")}</span>
        <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-slate-100 border border-slate-200" /> {t("insights.noTrades")}</span>
      </div>
    </div>
  );
}

function DayDetail({ dateKey, day, t, lang }) {
  const label = new Date(`${dateKey}T12:00:00`).toLocaleDateString(lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  return (
    <div data-testid="insights-day-detail" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-sm font-semibold text-slate-900 first-letter:uppercase">{label}</div>
      {!day ? <p className="mt-2 text-sm text-slate-500">{t("insights.noTradesDay")}</p> : (
        <ul className="mt-3 divide-y divide-slate-100">
          {day.trades.map((e) => (
            <li key={e.id} className="py-2 flex items-center gap-3 text-sm">
              <span className="font-mono font-semibold text-slate-900 w-20">{e.instrument}</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{e.direction}</span>
              {e.entry_time && <span className="text-xs text-slate-400 font-mono">{e.entry_time}</span>}
              <span className={`ml-auto font-mono font-semibold ${tone(e.pnl || 0)}`}>{money(Number(e.pnl) || 0)}</span>
              {e.r_multiple != null && <span className="text-xs text-slate-500 font-mono w-12 text-right">{Number(e.r_multiple).toFixed(1)}R</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function statFor(list) {
  const pnl = list.reduce((s, e) => s + (Number(e.pnl) || 0), 0);
  const wins = list.filter((e) => (Number(e.pnl) || 0) > 0).length;
  return { n: list.length, pnl, wr: list.length ? Math.round((wins / list.length) * 100) : 0 };
}

function WeekdayStats({ entries, t, lang }) {
  const dayFmt = new Intl.DateTimeFormat(lang, { weekday: "long" });
  const rows = WEEKDAYS.map((wd) => ({ wd, ...statFor(entries.filter((e) => e.traded_on && new Date(`${e.traded_on}T12:00:00`).getDay() === wd)) })).filter((r) => r.n);
  const best = rows.length ? rows.reduce((a, b) => (b.pnl > a.pnl ? b : a)) : null;
  return (
    <div data-testid="insights-weekday-stats" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="text-sm font-semibold text-slate-900">{t("insights.byWeekday")}</div>
      {!rows.length ? <p className="mt-2 text-sm text-slate-500">{t("insights.empty")}</p> : (
        <table className="mt-3 w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-widest text-slate-400">
            <th className="text-left font-semibold py-1">{t("insights.day")}</th><th className="text-right font-semibold">{t("insights.trades")}</th>
            <th className="text-right font-semibold">{t("insights.winRate")}</th><th className="text-right font-semibold">P&L</th></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.wd} className={`border-t border-slate-100 ${best?.wd === r.wd ? "bg-emerald-50/60" : ""}`}>
              <td className="py-2 capitalize text-slate-800">{dayFmt.format(new Date(2024, 0, r.wd === 0 ? 7 : r.wd))}</td>
              <td className="text-right font-mono">{r.n}</td><td className="text-right font-mono">{r.wr}%</td>
              <td className={`text-right font-mono font-semibold ${tone(r.pnl)}`}>{money(r.pnl)}</td></tr>
          ))}</tbody>
        </table>
      )}
    </div>
  );
}

function SessionGrid({ entries, t, lang }) {
  const dayFmt = new Intl.DateTimeFormat(lang, { weekday: "short" });
  const timed = entries.filter((e) => e.traded_on && e.entry_time);
  const cell = (wd, sid) => statFor(timed.filter((e) => {
    const d = new Date(`${e.traded_on}T${e.entry_time}`);
    return d.getDay() === wd && primarySession(d) === sid;
  }));
  const color = (c) => (!c.n ? "bg-slate-50 text-slate-300" : c.pnl > 0 ? "bg-emerald-100 text-emerald-800" : c.pnl < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-600");
  return (
    <div data-testid="insights-session-grid" className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <div className="text-sm font-semibold text-slate-900">{t("insights.bySession")}</div>
        <span className="ml-auto text-[11px] text-slate-400">{t("insights.timedCount", { count: timed.length, total: entries.length })}</span>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-xs border-separate border-spacing-1">
          <thead><tr><th />{SESSION_COLS.map((s) => <th key={s} className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{t(`sessions.${s}`)}</th>)}</tr></thead>
          <tbody>{[1, 2, 3, 4, 5].map((wd) => (
            <tr key={wd}>
              <td className="pr-2 text-slate-500 capitalize">{dayFmt.format(new Date(2024, 0, wd))}</td>
              {SESSION_COLS.map((sid) => {
                const c = cell(wd, sid);
                return (
                  <td key={sid} data-testid={`insights-cell-${wd}-${sid}`} className={`rounded-lg text-center py-2 ${color(c)}`}>
                    {c.n ? <><div className="font-mono font-semibold">{c.wr}%</div><div className="text-[10px] opacity-80">{c.n} · {money(c.pnl)}</div></> : "·"}
                  </td>
                );
              })}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {!timed.length && <p className="mt-2 text-xs text-slate-500">{t("insights.addTimeHint")}</p>}
    </div>
  );
}

export default function TradeInsights({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [entries, setEntries] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [selected, setSelected] = useState(iso(new Date()));
  const [reviewOpen, setReviewOpen] = useState(false);
  const thisMonth = iso(new Date()).slice(0, 7);
  const lastMonth = prevMonth(thisMonth);
  const [period, setPeriod] = useState("year");
  const [month, setMonth] = useState(new Date().getDate() <= 7 ? lastMonth : thisMonth);
  useEffect(() => { api.get("/journal/entries", { params: { limit: 2000 } }).then((r) => setEntries(r.data || [])).catch(() => {}); }, []);
  const data = useYearData(entries, year);
  const yearEntries = useMemo(() => entries.filter((e) => (e.traded_on || "").startsWith(String(year))), [entries, year]);
  const years = useMemo(() => {
    const ys = new Set([new Date().getFullYear(), ...entries.map((e) => Number((e.traded_on || "").slice(0, 4))).filter(Boolean)]);
    return [...ys].sort((a, b) => b - a);
  }, [entries]);
  const months = useMemo(() => {
    const ms = new Set([thisMonth, lastMonth, month, ...entries.map((e) => (e.traded_on || "").slice(0, 7)).filter((m) => m.length === 7)]);
    return [...ms].sort().reverse();
  }, [entries, thisMonth, lastMonth, month]);
  const lastMonthCount = entries.filter((e) => (e.traded_on || "").startsWith(lastMonth)).length;
  const [params] = useSearchParams();
  useEffect(() => {
    const ym = params.get("ym");
    if (params.get("review") === "month" && /^\d{4}-\d{2}$/.test(ym || "")) { setPeriod("month"); setMonth(ym); setReviewOpen(true); }
  }, [params]);
  const openMonth = () => { setPeriod("month"); setMonth(lastMonth); setReviewOpen(true); };

  return (
    <div data-testid="resources-insights" className="space-y-5">
      <MonthReadyBanner ym={lastMonth} count={lastMonthCount} onOpen={openMonth} />
      <InsightsGuide entries={entries} onNavigate={onNavigate} />
      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Activity size={16} className="text-blue-800" />
          <div className="text-sm font-semibold text-slate-900">{t("insights.yearTitle", { year })}</div>
          <button type="button" data-testid="insights-year-review-btn" onClick={() => { setPeriod("year"); setReviewOpen(true); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-950 text-amber-400 text-xs font-semibold hover:bg-slate-800 transition-colors">
            <Sparkles size={12} /> {t("insights.review.open")}
          </button>
          <button type="button" data-testid="insights-month-review-btn" onClick={() => { setPeriod("month"); setReviewOpen(true); }}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-slate-200 text-slate-800 text-xs font-semibold hover:bg-slate-50 transition-colors">
            <Sparkles size={12} className="text-amber-500" /> {t("insights.review.monthOpen")}
          </button>
          <div className="ml-auto flex items-center gap-1">
            <button type="button" data-testid="insights-prev-year" onClick={() => setYear((y) => y - 1)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronLeft size={14} /></button>
            <button type="button" data-testid="insights-today" onClick={() => { setYear(new Date().getFullYear()); setSelected(iso(new Date())); }} className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50">{t("insights.today")}</button>
            <button type="button" data-testid="insights-next-year" onClick={() => setYear((y) => y + 1)} className="h-8 w-8 inline-flex items-center justify-center rounded-lg border border-slate-200 hover:bg-slate-50"><ChevronRight size={14} /></button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip testId="insights-chip-pnl" label={t("insights.yearPnl")} value={money(data.total)} cls={tone(data.total)} />
          <Chip testId="insights-chip-trades" label={t("insights.trades")} value={data.trades} />
          <Chip label={t("insights.winningDays")} value={data.win} />
          <Chip label={t("insights.losingDays")} value={data.loss} />
          <Chip label={t("insights.bestDay")} value={money(data.bestDay)} cls={tone(data.bestDay)} />
          <Chip label={t("insights.worstDay")} value={money(data.worstDay)} cls={tone(data.worstDay)} />
          <Chip label={t("insights.bestWeek")} value={money(data.bestWeek)} cls={tone(data.bestWeek)} />
          <Chip label={t("insights.worstWeek")} value={money(data.worstWeek)} cls={tone(data.worstWeek)} />
        </div>
        <YearGrid data={data} year={year} selected={selected} onSelect={setSelected} t={t} lang={lang} />
      </div>
      <DayDetail dateKey={selected} day={data.days[selected]} t={t} lang={lang} />
      <div className="grid gap-5 2xl:grid-cols-2">
        <WeekdayStats entries={yearEntries} t={t} lang={lang} />
        <SessionGrid entries={yearEntries} t={t} lang={lang} />
      </div>
      <YearReviewDialog open={reviewOpen} onOpenChange={setReviewOpen} entries={entries} years={years} year={year} setYear={setYear}
        months={months} period={period} setPeriod={setPeriod} month={month} setMonth={setMonth} />
    </div>
  );
}
